import { Color, Group } from 'three/webgpu'
import { easing } from 'maath'
import GlassMaterial from '../materials/GlassMaterial'
import GridTunnel from './GridTunnel'

export default class XrayMode extends Group {
	static defaults = {
		device: null,
		scene: null,
		texts: [],
		trigger: 'button_stop',
		cases: ['front_case_Baked', 'back_case_Baked'],
		bodyClass: 'is-inverted',
		canvas: 0xe5e3e0,
		background: 0x000000,
		ink: 0xffffff,
		smoothing: 0.18,
		glass: {},
		tunnel: {},
	}

	constructor(options = {}) {
		super()

		this.options = { ...XrayMode.defaults, ...options }
		this.progress = 0
		this.active = false
		this.on = false
		this.tint = new Color()
		this.canvas = new Color(this.options.canvas)
		this.background = new Color(this.options.background)
		this.ink = new Color(this.options.ink)
		this.material = new GlassMaterial(this.options.glass)
		this.tunnel = new GridTunnel(this.options.tunnel)

		this.add(this.tunnel)
		this.peak = {
			thickness: this.material.thickness,
			dispersion: this.material.dispersion,
			iridescence: this.material.iridescence,
		}
	}

	#attach() {
		const { device, trigger, cases } = this.options

		this.trigger = device?.control(trigger)

		if (!this.trigger) return false

		this.cases = cases.map(name => device.mesh(name)).filter(Boolean)
		this.solid = this.cases.map(mesh => mesh.material)

		return true
	}

	#swap(active) {
		this.active = active

		this.cases.forEach((mesh, index) => {
			mesh.material = active ? this.material : this.solid[index]
			mesh.castShadow = !active
		})
	}

	#apply(elapsed) {
		const { scene, texts } = this.options
		const progress = this.progress

		scene.background?.copy(this.canvas).lerp(this.background, progress)

		for (const text of texts) {
			this.tint.copy(text.tone).lerp(this.ink, progress)
			text.color = this.tint
		}

		this.material.thickness = this.peak.thickness * progress
		this.material.dispersion = this.peak.dispersion * progress
		this.material.iridescence = this.peak.iridescence * progress
		this.material.time = elapsed

		this.tunnel.progress = progress
		this.tunnel.time = elapsed
	}

	update(delta, elapsed) {
		if (!this.trigger && !this.#attach()) return

		const down = !!this.trigger.pressed

		if (down !== this.down) {
			this.down = down

			if (down) {
				this.on = !this.on
				document.body.classList.toggle(this.options.bodyClass, this.on)
			}
		}

		const pressed = this.on

		if (!pressed && this.progress < 0.001) {
			if (this.active) this.#swap(false)

			if (!this.settled) {
				this.settled = true
				this.progress = 0
				this.#apply(elapsed)
			}

			return
		}

		this.settled = false

		if (pressed && !this.active) this.#swap(true)

		easing.damp(this, 'progress', pressed ? 1 : 0, this.options.smoothing, delta)
		this.#apply(elapsed)
	}

	dispose() {
		document.body.classList.remove(this.options.bodyClass)
		this.on = false

		if (this.active) this.#swap(false)

		this.progress = 0
		this.tunnel.dispose()
		this.material.dispose()
		this.trigger = null
		this.cases = null
		this.solid = null
		this.removeFromParent()
	}
}
